import React, { useEffect, useState } from 'react';
import {
    IconButton, useTheme, useMediaQuery, Snackbar, Fade, Alert, Box
} from '@mui/material';
import { API } from 'aws-amplify';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import TabsLayout from '../Common/TabsLayout';
import DataComponent from './DataComponent';
import ClientManagement from './ClientManagement';
import { deleteDropDownData } from '../Queries';

const api_name = process.env.REACT_APP_STAGE === "DEV" ? process.env.REACT_APP_API_NAME_DEV : process.env.REACT_APP_API_NAME_PROD;

const DataManagement = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));

    // Consistent styles with UserManagement
    const formStyles = {
        textField: {
            '& .MuiOutlinedInput-root': {
                height: '44px',
                fontFamily: 'Poppins',
                fontSize: '14px',
                backgroundColor: '#fff',
                borderRadius: '8px',
                transition: 'all 0.2s ease',
                '& fieldset': {
                    borderColor: '#E5E7EB',
                    borderRadius: '8px',
                },
                '&:hover fieldset': {
                    borderColor: '#2563EB'
                },
                '&.Mui-focused fieldset': {
                    borderColor: '#2563EB',
                    borderWidth: '1px'
                }
            },
            '& .MuiInputLabel-root': {
                fontFamily: 'Poppins',
                fontSize: '14px',
                color: '#6B7280',
                fontWeight: 500,
                '&.MuiInputLabel-shrink': {
                    backgroundColor: '#fff',
                    padding: '0 8px'
                }
            },
            '& .MuiOutlinedInput-input': {
                padding: '12px 16px',
                color: '#374151',
                letterSpacing: '0.2px'
            }
        },
        select: {
            '& .MuiOutlinedInput-root': {
                height: '44px',
                borderRadius: '8px',
                backgroundColor: '#fff',
                transition: 'all 0.2s ease',
                '& fieldset': {
                    borderColor: '#E5E7EB',
                    borderRadius: '8px',
                },
                '&:hover fieldset': {
                    borderColor: '#2563EB'
                },
                '&.Mui-focused fieldset': {
                    borderColor: '#2563EB',
                    borderWidth: '1px'
                },
                '& .MuiSelect-icon': {
                    color: '#6B7280',
                    right: 12
                }
            },
            '& .MuiSelect-select': {
                fontFamily: 'Poppins',
                fontSize: '14px',
                padding: '12px 16px !important',
                backgroundColor: '#fff',
                height: '20px !important',
                minHeight: 'unset !important',
                display: 'flex',
                alignItems: 'center',
                color: '#374151',
                letterSpacing: '0.2px'
            }
        },
        modalTitle: {
            fontFamily: 'Poppins',
            fontSize: '18px',
            fontWeight: 600,
            color: '#333',
            marginBottom: '24px'
        }
    };

    const [activeTab, setActiveTab] = useState('clients');

    const [registerData, setRegisterData] = useState({
        id: '',
        name: '',
        type: '',
        client_code: '',
        json_data: [{
            activityKey: 'question-activity',
            questionId: 1,
            title: '',
            questionText: '',
            options: [''],
            answerIndex: 0,
            explanation: '',
        }],
        image: '',
        created_at: '',
        updated_at: '',
        created_by: localStorage.getItem("user") || '',
    });

    const [tableData, setTableData] = useState([]);
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [editData, setEditData] = useState(false);
    const [alert, setAlert] = useState({
        open: false,
        severity: 'success',
        message: '',
    });

    const tabs = [
        {
            id: 'clients',
            label: 'Clients',
            title: 'Client Management',
            description: 'Manage and maintain client information including client details, logos, and page access privileges.'
        },
        {
            id: 'batch-code',
            label: 'Batch Code',
            title: 'Batch Code Management',
            description: 'Create and manage batch codes for different programs and courses to organize training sessions.'
        },
        {
            id: 'registration-type',
            label: 'Registration Type',
            title: 'Registration Type Management',
            description: 'Define and manage different registration types to categorize user enrollments and course participation.'
        },
        {
            id: 'activities',
            label: 'Activities',
            title: 'Activity Management',
            description: 'Create and manage interactive activities, questions, and assessments for training programs.'
        },
        {
            id: 'micro-learning',
            label: 'Micro Learning',
            title: 'Micro Learning Content',
            description: 'Organize and manage bite-sized learning content and video resources for efficient skill development.'
        },
        {
            id: 'evaluation-matrix',
            label: 'Evaluation Matrix',
            title: 'Evaluation Matrix Management',
            description: 'Configure evaluation criteria and matrices for assessing user performance across different skill areas.'
        }
    ];

    const columns = [
        {
            field: 'S. No',
            headerName: 'S. No',
            width: isSmallScreen ? 50 : 70,
            renderCell: (params) => params.row.id
        },
        {
            field: 'name',
            headerName: 'Name',
            width: isSmallScreen ? 200 : 350,
            flex: isMobile ? 1 : 0
        },
        {
            field: 'created_at',
            headerName: 'Created',
            width: isSmallScreen ? 100 : 150,
            hide: isSmallScreen
        },
        {
            field: 'updated_at',
            headerName: 'Updated',
            width: isSmallScreen ? 100 : 150,
            hide: isSmallScreen
        },
        {
            field: 'created_by',
            headerName: 'Created By',
            width: isSmallScreen ? 150 : 300,
            hide: isSmallScreen
        },
        {
            field: 'actions',
            headerName: 'Actions',
            width: isSmallScreen ? 80 : 100,
            sortable: false,
            renderCell: (params) => (
                <Box sx={{ display: 'flex', gap: 0.5 }}>
                    <IconButton
                        onClick={() => handleEdit(params.row)}
                        size={isSmallScreen ? 'small' : 'medium'}
                        sx={{
                            color: '#0a0068',
                            '&:hover': { backgroundColor: 'rgba(10, 0, 104, 0.1)' }
                        }}
                    >
                        <EditIcon fontSize={isSmallScreen ? 'small' : 'medium'} />
                    </IconButton>
                    <IconButton
                        onClick={() => handleDeleteConfirmation(params.row)}
                        size={isSmallScreen ? 'small' : 'medium'}
                        sx={{
                            color: '#cc4049',
                            '&:hover': { backgroundColor: 'rgba(204, 64, 73, 0.1)' }
                        }}
                    >
                        <DeleteIcon fontSize={isSmallScreen ? 'small' : 'medium'} />
                    </IconButton>
                </Box>
            ),
        },
    ];

    useEffect(() => {
        if (editData && registerData.id) {
            setIsLoading(false);
        }
    }, [editData, registerData]);

    const handleCloseAlert = () => {
        setAlert({
            ...alert,
            open: false,
        });
        setRegisterData({
            id: '',
            name: '',
            type: '',
            client_code: '',
            created_at: '',
            updated_at: '',
            created_by: localStorage.getItem("user") || '',
        });
        setOpen(false);
    };

    const handleSuccess = (message) => {
        setAlert({
            open: true,
            severity: 'success',
            message,
        });
        setLoading(false);
        setIsLoading(false);
    };

    const handleError = (message) => {
        setAlert({
            open: true,
            severity: 'error',
            message,
        });
        setLoading(false);
        setIsLoading(false);
    };

    const handleChange = (event) => {
        setRegisterData({
            ...registerData,
            [event.target.name]: event.target.value,
        });
    };

    const handleEdit = (data) => {
        setEditData(true);
        setRegisterData({
            id: data.row_id,
            name: data.name,
            image: data.image,
            json_data: data.json_data,
            client_code: data.client_code || '',
            description: data.description || '',
            created_by: data.created_by || localStorage.getItem("user") || ''
        });
        setOpen(true);
    };

    const handleDeleteConfirmation = (row) => {
        if (window.confirm(`Are you sure you want to delete "${row.name}"?`)) {
            handleDelete(row.row_id, row.type);
        }
    };

    const handleDelete = async (id, data_type) => {
        setLoading(true);
        setIsLoading(true);

        try {
            const response = await deleteDropDownData(id, data_type);
            
            if (response.data && response.data.statusCode === 200) {
                const result = JSON.parse(response.data.body);
                getTableData();
                handleSuccess(result.message || 'Data deleted successfully');
            } else {
                handleError('Unable to delete the Data. Please try again.');
            }
        } catch (error) {
            handleError('Unable to delete the Data. Please try again.');
        } finally {
            setLoading(false);
            setIsLoading(false);
        }
    };

    const handleSubmit = async (event) => {
        setLoading(true);
        setIsLoading(true);
        const currentUser = localStorage.getItem("user");

        if (event.component && event.component === "micro_learning") {
            if (event.result) {
                getTableData();
                handleSuccess(event.result);
            } else {
                handleError(event.error);
            }
        } else {
            if (activeTab === 'activities') {
                setRegisterData({
                    ...registerData,
                    "json_data": event.questions,
                    "name": event.title
                });
            } else if (activeTab === 'batch-code') {
                setRegisterData({
                    ...registerData,
                    "image": event.image,
                    "name": event.name,
                    "description": event.description
                });
            } else if (activeTab === 'evaluation-matrix') {
                setRegisterData({
                    ...registerData,
                    "type": event.type,
                    "name": event.name
                });
            }
            let apiName = api_name;
            let path = '';
            let params = {
                body: {
                    "command": editData ? "updateDropDownData" : "addDropDownData",
                    "id": editData ? registerData.id : new Date().getTime().toString(),
                    "data_name": activeTab === 'activities' ? event.title : activeTab === 'batch-code' ? event.name : registerData.name,
                    "data_type": activeTab === 'batch-code' ? "program_code" : activeTab === 'registration-type' ? "registration_course" : activeTab === 'activities' ? "activity" : activeTab === 'micro-learning' ? "micro_learning" : registerData.type,
                    "json_data": activeTab === 'activities' ? JSON.stringify(event.questions) : activeTab === 'batch-code' ? JSON.stringify({ description: event.description || registerData.description || "" }) : "{}",
                    "image": activeTab === 'batch-code' ? event.image : registerData.image,
                    "client_code": event.client_code || "",
                    "created_at": new Date().toLocaleDateString(),
                    "updated_at": new Date().toLocaleDateString(),
                    "created_by": editData ? (registerData.created_by || currentUser) : currentUser,
                },
                response: true,
                convertEmptyValues: true,
                headers: {}
            };

            API.post(apiName, path, params).then(response => {
                if (response.data.statusCode === 200) {
                    let result = JSON.parse(response.data.body);
                    getTableData();
                    handleSuccess(result.message);
                } else {
                    handleError('Unable to add the Data. Please try again.');
                }
            }).catch(error => {
                handleError('Unable to add the data. Please try again.');
            });
        }
    };

    const handleOpenModal = () => {
        setEditData(false);
        setOpen(true);
        setRegisterData({
            id: '',
            name: '',
            type: '',
            client_code: '',
            json_data: [{
                activityKey: 'question-activity',
                questionId: 1,
                title: '',
                questionText: '',
                options: [''],
                answerIndex: 0,
                explanation: '',
            }],
            created_at: '',
            updated_at: '',
            created_by: localStorage.getItem("user") || '',
        });
    };

    const handleCloseModal = () => {
        setOpen(false);
    };

    const getTableData = () => {
        let apiName = api_name;
        let path = '';
        let params = {
            body: {
                "command": "getAllDropDownData"
            },
            response: true,
            convertEmptyValues: true,

            headers: {}
        };

        API.post(apiName, path, params).then(response => {
            if (response.status === 200) {
                if (response.data) {
                    let result = JSON.parse(response.data.body);
                    let resultData = [];

                    result.users.map((item, index) => {
                        let jsonData = item.json_data ? JSON.parse(item.json_data.S) : {};
                        let data = {
                            id: index + 1,
                            row_id: item.id.S,
                            name: item.data_name.S,
                            type: item.data_type.S,
                            json_data: jsonData,
                            image: item.image ? item.image.S : "",
                            client_code: item.client_code ? item.client_code.S : "",
                            description: item.data_type.S === "program_code" ? (jsonData.description || "") : "", // Extract description from json_data for batch codes
                            created_at: item.created_at.S,
                            updated_at: item.updated_at.S,
                            created_by: item.created_by.S,
                        };
                        resultData.push(data);
                    });
                    setTableData(resultData);
                    setIsLoading(false);
                } else {
                    setIsLoading(false);
                }
            }
        }).catch(error => {
            console.log(error, 'Unable to load the data. Please try again.');
        });
    };

    useEffect(() => {
        setIsLoading(true);
        getTableData();
    }, []);

    const renderTabContent = (activeTabId) => {
        switch (activeTabId) {
            case 'clients':
                return <ClientManagement />;
            case 'batch-code':
                return (
                    <DataComponent
                        tabLabel="Batch Code"
                        tableData={tableData}
                        columns={columns}
                        loading={isLoading}
                        submitLoading={loading}
                        alert={alert}
                        activeTab={1}
                        open={open}
                        handleOpenModal={handleOpenModal}
                        handleCloseModal={handleCloseModal}
                        handleChange={handleChange}
                        handleSubmit={handleSubmit}
                        handleCloseAlert={handleCloseAlert}
                        registerData={registerData}
                        editData={editData}
                        handleEdit={handleEdit}
                        handleDelete={handleDeleteConfirmation}
                    />
                );
            case 'registration-type':
                return (
                    <DataComponent
                        tabLabel="Registration Type"
                        tableData={tableData}
                        columns={columns}
                        loading={isLoading}
                        submitLoading={loading}
                        alert={alert}
                        activeTab={2}
                        open={open}
                        handleOpenModal={handleOpenModal}
                        handleCloseModal={handleCloseModal}
                        handleChange={handleChange}
                        handleSubmit={handleSubmit}
                        handleCloseAlert={handleCloseAlert}
                        registerData={registerData}
                        handleDelete={handleDeleteConfirmation}
                    />
                );
            case 'activities':
                return (
                    <DataComponent
                        key={registerData.id}
                        tabLabel="Activities"
                        tableData={tableData}
                        columns={columns}
                        loading={isLoading}
                        submitLoading={loading}
                        alert={alert}
                        activeTab={3}
                        open={open}
                        handleOpenModal={handleOpenModal}
                        handleCloseModal={handleCloseModal}
                        handleChange={handleChange}
                        handleSubmit={handleSubmit}
                        handleCloseAlert={handleCloseAlert}
                        registerData={registerData}
                        handleDelete={handleDeleteConfirmation}
                    />
                );
            case 'micro-learning':
                return (
                    <DataComponent
                        key={registerData.id}
                        tabLabel="Micro Learning"
                        tableData={tableData}
                        columns={columns}
                        loading={isLoading}
                        submitLoading={loading}
                        alert={alert}
                        activeTab={4}
                        open={open}
                        handleOpenModal={handleOpenModal}
                        handleCloseModal={handleCloseModal}
                        handleChange={handleChange}
                        handleSubmit={handleSubmit}
                        handleCloseAlert={handleCloseAlert}
                        registerData={registerData}
                        editData={editData}
                        handleDelete={handleDeleteConfirmation}
                    />
                );
            case 'evaluation-matrix':
                return (
                    <DataComponent
                        key={registerData.id}
                        tabLabel="Evaluation Matrix"
                        tableData={tableData}
                        columns={columns}
                        loading={isLoading}
                        submitLoading={loading}
                        alert={alert}
                        activeTab={5}
                        open={open}
                        handleOpenModal={handleOpenModal}
                        handleCloseModal={handleCloseModal}
                        handleChange={handleChange}
                        handleSubmit={handleSubmit}
                        handleCloseAlert={handleCloseAlert}
                        registerData={registerData}
                        editData={editData}
                        handleDelete={handleDeleteConfirmation}
                    />
                );
            default:
                return <ClientManagement />;
        }
    };

    return (
        <div style={{ background: 'white', minHeight: '100vh' }}>
            <TabsLayout
                tabs={tabs}
                title="Data Management"
                defaultTab="clients"
                onTabChange={setActiveTab}
                className="h-screen"
            >
                {renderTabContent}
            </TabsLayout>

            {/* Enhanced Snackbar for alerts */}
            <Snackbar
                open={alert.open}
                autoHideDuration={4000}
                onClose={handleCloseAlert}
                anchorOrigin={{
                    vertical: isMobile ? 'top' : 'bottom',
                    horizontal: 'center'
                }}
                TransitionComponent={Fade}
            >
                <Alert
                    onClose={handleCloseAlert}
                    severity={alert.severity}
                    variant="filled"
                    sx={{
                        width: '100%',
                        maxWidth: isMobile ? '90vw' : '400px',
                        borderRadius: 2,
                    }}
                >
                    {alert.message}
                </Alert>
            </Snackbar>
        </div>
    );
};


export default DataManagement;